// canvasUtils.js
// Canvas helpers: clone and crop operations for screenshot rendering.
//
// Copyright Viktor Eberhardt, 2025
// Author: Viktor Eberhardt

// Framework imports

// External imports

// MUI imports (none)

// HSA imports (none)

// Crop away black margins while ignoring sparse edge pixels (e.g., overlay lines)
// Returns the original canvas if no crop needed
export function cropCanvasToNonBlack(sourceCanvas, opts = {}) {
  const {
    threshold = 10,
    minAlpha = 1,
    pad = 2,
    rowMinFrac = 0.01, // min fraction of width for top/bottom to consider row as content
    colMinFrac = 0.01, // min fraction of height for left/right to consider column as content
    scaleBarMaxWidth = null, // if provided, ignore rows with non-black only in this rightmost band
  } = opts;
  if (!sourceCanvas) return sourceCanvas;
  const w = sourceCanvas.width | 0;
  const h = sourceCanvas.height | 0;
  if (w <= 0 || h <= 0) return sourceCanvas;

  const ctx = sourceCanvas.getContext("2d", { willReadFrequently: true });
  const { data } = ctx.getImageData(0, 0, w, h);
  const stride = w * 4;

  const isNonBlack = (idx) => {
    const a = data[idx + 3];
    if (a < minAlpha) return false;
    const r = data[idx];
    const g = data[idx + 1];
    const b = data[idx + 2];
    return r > threshold || g > threshold || b > threshold;
  };

  const rowMinCount = Math.max(3, Math.floor(w * rowMinFrac));
  const colMinCount = Math.max(3, Math.floor(h * colMinFrac));

  let top = 0;
  for (let y = 0; y < h; y++) {
    const row = y * stride;
    let cnt = 0;
    let minX = w,
      maxX = -1;
    for (let x = 0; x < w && cnt < rowMinCount; x++) {
      if (isNonBlack(row + x * 4)) {
        cnt++;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
      }
    }
    let isOnlyScaleBarBand = false;
    if (scaleBarMaxWidth && maxX >= 0) {
      const bandStart = w - (scaleBarMaxWidth | 0) - 8; // small tolerance
      if (minX >= bandStart && maxX - minX + 1 <= (scaleBarMaxWidth | 0) + 8) {
        isOnlyScaleBarBand = true;
      }
    }
    if (cnt >= rowMinCount && !isOnlyScaleBarBand) {
      top = y;
      break;
    }
    if (y === h - 1) return sourceCanvas; // all black
  }

  let bottom = h - 1;
  for (let y = h - 1; y >= top; y--) {
    const row = y * stride;
    let cnt = 0;
    let minX = w,
      maxX = -1;
    for (let x = 0; x < w && cnt < rowMinCount; x++) {
      if (isNonBlack(row + x * 4)) {
        cnt++;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
      }
    }
    let isOnlyScaleBarBand = false;
    if (scaleBarMaxWidth && maxX >= 0) {
      const bandStart = w - (scaleBarMaxWidth | 0) - 8;
      if (minX >= bandStart && maxX - minX + 1 <= (scaleBarMaxWidth | 0) + 8) {
        isOnlyScaleBarBand = true;
      }
    }
    if (cnt >= rowMinCount && !isOnlyScaleBarBand) {
      bottom = y;
      break;
    }
  }

  let left = 0;
  for (let x = 0; x < w; x++) {
    let cnt = 0;
    for (let y = top; y <= bottom && cnt < colMinCount; y++) {
      if (isNonBlack(y * stride + x * 4)) cnt++;
    }
    if (cnt >= colMinCount) {
      left = x;
      break;
    }
  }

  let right = w - 1;
  for (let x = w - 1; x >= left; x--) {
    let cnt = 0;
    for (let y = top; y <= bottom && cnt < colMinCount; y++) {
      if (isNonBlack(y * stride + x * 4)) cnt++;
    }
    if (cnt >= colMinCount) {
      right = x;
      break;
    }
  }

  // Add a small padding to avoid clipping overlays like the scale bar
  left = Math.max(0, left - pad);
  top = Math.max(0, top - pad);
  right = Math.min(w - 1, right + pad);
  bottom = Math.min(h - 1, bottom + pad);

  const cw = right - left + 1;
  const ch = bottom - top + 1;
  if (cw <= 0 || ch <= 0) return sourceCanvas;
  if (cw === w && ch === h) return sourceCanvas; // nothing to crop

  const out = document.createElement("canvas");
  out.width = cw;
  out.height = ch;
  const outCtx = out.getContext("2d");
  outCtx.drawImage(sourceCanvas, left, top, cw, ch, 0, 0, cw, ch);
  return out;
}

// Clone the active renderer canvas to an offscreen canvas
export async function captureCanvasClone(FV) {
  const r = FV?.rendererDict?.[FV.activeFileId];
  if (!r || !r.canvas) return null;
  const base = r.canvas;
  const off = document.createElement("canvas");
  off.width = base.width;
  off.height = base.height;
  const ctx = off.getContext("2d");
  ctx.drawImage(base, 0, 0);
  return off;
}

export default {
  cropCanvasToNonBlack,
  captureCanvasClone,
};
